import React, { useState } from 'react';
import ModuleLayout from '../../layouts/ModuleLayout';
import brandingConfig from '../../config/brandingConfig';
import {
  Smartphone,
  Download,
  QrCode,
  CheckCircle2,
  Zap,
  Wifi,
  Battery,
  ShieldCheck,
  ChefHat,
  Bell,
  Receipt,
  Users,
  Clock,
  Star,
  RefreshCw,
  ExternalLink,
  Info,
  Play,
  Apple,
  Tablet,
  WifiOff,
  Send,
} from 'lucide-react';

/**
 * Captain App Page
 * Landing page for the Captain/Waiter mobile app download
 * Captain App {`by ${brandingConfig.productName}`}
 */

// App Features
const APP_FEATURES = [
  {
    icon: Receipt,
    title: 'Quick Order Taking',
    description: 'Take orders directly at the table with intuitive interface',
  },
  {
    icon: Send,
    title: 'Instant KOT',
    description: 'Send orders instantly to kitchen display system',
  },
  {
    icon: Bell,
    title: 'Real-time Alerts',
    description: 'Get notified when orders are ready for serving',
  },
  {
    icon: Tablet,
    title: 'Table Management',
    description: 'View and manage table status on the go',
  },
  {
    icon: Clock,
    title: 'Order History',
    description: 'Access past orders and customer preferences',
  },
  {
    icon: WifiOff,
    title: 'Offline Mode',
    description: 'Works even without internet, syncs when back online',
  },
];

// Device Requirements
const DEVICE_REQUIREMENTS = [
  { label: 'Android', value: 'Android 8.0 (Oreo) or later' },
  { label: 'iOS', value: 'iOS 13.0 or later' },
  { label: 'RAM', value: 'Minimum 2GB RAM' },
  { label: 'Storage', value: '100MB free space' },
  { label: 'Screen', value: 'Recommended 5" or larger' },
];

// App Stats
const APP_STATS = [
  { value: '10,000+', label: 'Downloads' },
  { value: '4.8', label: 'Rating', icon: Star },
  { value: '50ms', label: 'Avg. Response' },
];

export default function CaptainAppPage() {
  const [showAppFallback, setShowAppFallback] = useState(false);
  const [showQrFallback, setShowQrFallback] = useState(false);
  const appScreenshotUrl = '/whitelabel/app.png';
  const qrCodeUrl = '/whitelabel/QR image.png';
  const androidPlayStoreUrl = 'https://play.google.com/store/apps/details?id=com.maverick.dine';

  const handleDownload = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <ModuleLayout>
      <div className="flex flex-col h-full bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold text-slate-900">Captain App</h1>
              <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">
                v2.5.0
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.location.reload()}
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                title="Refresh"
              >
                <RefreshCw size={18} />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Hero Section */}
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white px-6 py-10">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row items-center gap-8">
                {/* App Mockup */}
                <div className="flex-shrink-0">
                  <div className="w-[176px] h-[320px] overflow-hidden flex items-center justify-center">
                    {showAppFallback ? (
                      <div className="w-[176px] h-[320px] bg-gradient-to-b from-teal-500 to-teal-600 rounded-3xl flex flex-col items-center justify-center px-4 text-center">
                        <Smartphone size={40} className="text-white/90 mb-3" />
                        <h3 className="text-base font-bold">Captain App</h3>
                        <p className="text-xs text-white/80 mt-1">{`by ${brandingConfig.productName}`}</p>
                      </div>
                    ) : (
                      <img
                        src={appScreenshotUrl}
                        alt="Captain App mobile screen"
                        className="w-[350] h-[350px] object-contain max-w-none"
                        onError={() => setShowAppFallback(true)}
                      />
                    )}
                  </div>
                </div>

                {/* Hero Content */}
                <div className="flex-1 text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                    <h1 className="text-2xl md:text-3xl font-bold">
                      Captain App
                    </h1>
                    <span className="text-teal-200 text-sm">{`by ${brandingConfig.productName}`}</span>
                  </div>
                  <p className="text-base text-teal-100 mb-5 max-w-md">
                    Empower your waiters to take orders, manage tables, and
                    communicate with the kitchen - all from their smartphone.
                  </p>

                  {/* Stats */}
                  <div className="flex items-center justify-center md:justify-start gap-6 mb-6">
                    {APP_STATS.map((stat, index) => (
                      <div key={index} className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-xl font-bold">{stat.value}</span>
                          {stat.icon && <Star size={14} className="text-amber-400 fill-amber-400" />}
                        </div>
                        <span className="text-xs text-teal-200">{stat.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Download Buttons */}
                  <div className="flex flex-col sm:flex-row items-center gap-3">
                    <button
                      onClick={() => handleDownload(androidPlayStoreUrl)}
                      className="w-full sm:w-auto flex items-center justify-center gap-3 px-5 py-2.5 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors shadow-lg"
                    >
                      <Play size={20} className="text-green-600" />
                      <div className="text-left">
                        <div className="text-[9px] uppercase text-slate-500">Get it on</div>
                        <div className="text-sm font-bold">Google Play</div>
                      </div>
                    </button>
                    <button
                      onClick={() => handleDownload('')}
                      className="w-full sm:w-auto flex items-center justify-center gap-3 px-5 py-2.5 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors shadow-lg"
                    >
                      <Apple size={20} />
                      <div className="text-left">
                        <div className="text-[9px] uppercase text-slate-500">Download on</div>
                        <div className="text-sm font-bold">App Store</div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-4xl mx-auto px-6 py-6">
            {/* QR Code Section */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
              <div className="flex flex-col md:flex-row items-center gap-5">
                {/* QR Code */}
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 bg-white rounded-xl flex items-center justify-center border-2 border-dashed border-slate-300 overflow-hidden">
                    {showQrFallback ? (
                      <div className="text-center">
                        <QrCode size={40} className="text-slate-400 mx-auto mb-1" />
                        <span className="text-[10px] text-slate-500">Scan to Download</span>
                      </div>
                    ) : (
                      <img
                        src={qrCodeUrl}
                        alt="Captain App QR code"
                        className="h-full w-full object-contain"
                        onError={() => setShowQrFallback(true)}
                      />
                    )}
                  </div>
                </div>

                {/* QR Instructions */}
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-base font-semibold text-slate-900 mb-1">
                    Quick Download via QR Code
                  </h3>
                  <p className="text-sm text-slate-600 mb-3">
                    Scan this QR code with your phone camera to instantly download
                    the Captain App. Works on both Android and iOS devices.
                  </p>
                  <div className="flex items-center justify-center md:justify-start gap-2 text-sm text-teal-600">
                    <ShieldCheck size={14} />
                    <span>Secure download from official app stores</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Grid */}
            <div className="mb-6">
              <h2 className="text-lg font-bold text-slate-900 mb-3">App Features</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {APP_FEATURES.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <div
                      key={index}
                      className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center mb-2">
                        <Icon size={18} className="text-teal-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 text-sm mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-xs text-slate-500">{feature.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Device Requirements */}
              <div>
                <h2 className="text-lg font-bold text-slate-900 mb-3">
                  Device Requirements
                </h2>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <table className="w-full">
                    <tbody className="divide-y divide-slate-100">
                      {DEVICE_REQUIREMENTS.map((req, index) => (
                        <tr key={index} className="hover:bg-slate-50">
                          <td className="px-4 py-2.5 text-xs font-medium text-slate-700">
                            {req.label}
                          </td>
                          <td className="px-4 py-2.5 text-xs text-slate-600">
                            {req.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Setup Instructions */}
              <div>
                <h2 className="text-lg font-bold text-slate-900 mb-3">
                  Setup Instructions
                </h2>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                  <ol className="space-y-3">
                    {[
                      'Download Captain App from Play Store / App Store',
                      'Open app and tap "Connect to Restaurant"',
                      'Scan QR code from Back Office > Settings > Devices',
                      'Enter your staff PIN to login',
                      'Start taking orders!',
                    ].map((step, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <div className="w-5 h-5 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                          {index + 1}
                        </div>
                        <span className="text-xs text-slate-700">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>

            {/* Help Section */}
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <Info size={18} className="text-teal-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-teal-900 text-sm mb-1">Need Help?</h3>
                  <p className="text-xs text-teal-700 mb-2">
                    Having trouble setting up the Captain App? Our support team is
                    here to help you get started.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="#"
                      className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-800"
                    >
                      <ExternalLink size={12} />
                      View Documentation
                    </a>
                    <a
                      href="#"
                      className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-800"
                    >
                      <ExternalLink size={12} />
                      Contact Support
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t border-slate-200 px-6 py-2">
          <p className="text-xs text-slate-500">
            {`Captain App ${brandingConfig.productVersion} by ${brandingConfig.productName} | `}
            <a href="#" className="text-teal-600 hover:text-teal-700">
              Release Notes
            </a>
          </p>
        </div>
      </div>
    </ModuleLayout>
  );
}

